import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Form,
  Input,
  Switch,
  Space,
  Typography,
  Tag,
  Popconfirm,
  message,
  Alert,
  Row,
  Col,
  Statistic,
  Select,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  MailOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  TeamOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { UserService, User, UserCreate, UserUpdate, UserFilters } from '../../services/api/userService';
import { CompanyInstance } from '../../types/companyInstance';
import { Company } from '../../types/company';
import { RoleService } from '../../services/api/roleService';
import { ROLES } from '../../constants/permissions';
import { SplitPaneLayout } from '../common/SplitPaneLayout';
import { ManagementForm } from '../common/ManagementForm';
import { ProfileDataEditor } from './ProfileDataEditor';
import {
  nestProfilePreferences,
  normalizeProfileData,
} from '../../utils/normalizeProfileData';
import { UserCompanyTab } from './UserCompanyTab';
import { UserSubscriptionTab } from './UserSubscriptionTab';
import { apiClient } from '../../services/apiClient';
import { getApiErrorMessage } from '../../utils/apiErrors';
import {
  ACCOUNT_MERGE_REFUSAL,
  duplicateEmailGroups,
  filterPlatformUsers,
  type MembershipRef,
} from '../AppShell/platformDirectory';
import '../../styles/management-pages.css';

const { Title, Text } = Typography;
const { Option } = Select;

interface UserManagementProps {
  className?: string;
}

export const UserManagement: React.FC<UserManagementProps> = ({ className }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userCompaniesMap, setUserCompaniesMap] = useState<Record<string, Array<{instance: CompanyInstance, company: Company}>>>({});
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string | undefined>();
  const [filters, setFilters] = useState<UserFilters>({});
  const [availableRoles, setAvailableRoles] = useState<Array<{value: string, description: string}>>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [directoryTruncated, setDirectoryTruncated] = useState(false);
  const [companyListTruncated, setCompanyListTruncated] = useState(false);
  const [membershipTruncated, setMembershipTruncated] = useState(false);

  const selectedUser = selectedUserId 
    ? users.find(u => u.id === selectedUserId) 
    : null;

  // Load users on component mount
  useEffect(() => {
    loadRoles();
    loadCompanies();
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [filters.role, filters.is_active]);

  const loadCompanies = async () => {
    try {
      const response = await apiClient.get<Company[]>('/crud/companies', { params: { limit: 1000 } });
      const companies = Array.isArray(response.data) ? response.data : [];
      setAllCompanies(companies);
      setCompanyListTruncated(companies.length >= 1000);
    } catch (error) {
      setAllCompanies([]);
      setCompanyListTruncated(false);
      message.error(getApiErrorMessage(error, 'Failed to load companies for the directory filter.'));
    }
  };

  const loadRoles = async () => {
    try {
      const roles = await RoleService.getAll();
      setAvailableRoles(roles);
    } catch (error) {
      console.error('Failed to load roles:', error);
      // Fallback to constants if API fails
      setAvailableRoles([
        { value: ROLES.SUPERUSER, description: 'Superuser' },
        { value: ROLES.ADMIN, description: 'Admin' },
        { value: ROLES.ARCHITECT, description: 'Architect' },
        { value: ROLES.MANAGER, description: 'Manager' },
        { value: ROLES.USER, description: 'User' },
        { value: ROLES.OPERATOR, description: 'Operator' },
        { value: ROLES.VIEWER, description: 'Viewer' },
      ]);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const userList = await UserService.listForPlatformDirectory({
        role: filters.role,
        is_active: filters.is_active,
        limit: 1000,
      });
      setUsers(userList);
      setDirectoryTruncated(userList.length >= 1000);

      const companiesMap: Record<string, Array<{instance: CompanyInstance, company: Company}>> = {};
      const pageSize = 1000;
      let offset = 0;
      let truncatedMemberships = false;
      for (let page = 0; page < 5; page += 1) {
        const response = await apiClient.get<Array<Record<string, unknown>>>('/company-instances', {
          params: { active_only: true, limit: pageSize, offset },
        });
        const batch = Array.isArray(response.data) ? response.data : [];
        for (const row of batch) {
          const userId = String(row.user_id || '');
          const companyId = String(row.company_id || '');
          if (!userId || !companyId) continue;
          const instance = {
            id: String(row.id || `${userId}-${companyId}`),
            user_id: userId,
            company_id: companyId,
            role: typeof row.role === 'string' ? row.role : undefined,
            is_active: true,
          } as CompanyInstance;
          const company = {
            id: companyId,
            name: String(row.company_name || companyId),
          } as Company;
          const existing = companiesMap[userId] || [];
          existing.push({ instance, company });
          companiesMap[userId] = existing;
        }
        if (batch.length < pageSize) {
          truncatedMemberships = false;
          break;
        }
        offset += pageSize;
        truncatedMemberships = true;
      }
      setMembershipTruncated(truncatedMemberships);
      setUserCompaniesMap(companiesMap);
    } catch (err: unknown) {
      const detail = getApiErrorMessage(err, 'Failed to load users');
      setDirectoryTruncated(false);
      setMembershipTruncated(false);
      setError(detail);
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    // Create a temporary user object for the list
    const tempId = `temp-${Date.now()}`;
    const newUser: User = {
      id: tempId,
      username: '',
      email: '',
      is_active: true,
      is_superuser: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    // Add to beginning of list
    setUsers([newUser, ...users]);
    setSelectedUserId(tempId);
    setIsCreating(true);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
    });
  };

  const handleSelectUser = (user: User) => {
    setSelectedUserId(user.id);
    setIsCreating(false);
    
    // Set form values
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      company_id: user.company_id,
      is_active: user.is_active,
      role: user.role,
      // Normalize JSON-string profile_data so the editor shows preferences, not characters.
      profile_data: normalizeProfileData(user.profile_data),
    });
  };

  const handleFilterChange = (key: keyof UserFilters, value: unknown) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === undefined || value === null || value === '' ? undefined : value,
    }));
  };

  const memberships: Record<string, MembershipRef[]> = {};
  for (const [userId, rows] of Object.entries(userCompaniesMap)) {
    memberships[userId] = rows.map((row) => ({
      companyId: row.company.id,
      companyName: row.company.name,
    }));
  }
  const filteredUsers = filterPlatformUsers(users, memberships, searchText, companyFilter);
  const duplicateEmails = duplicateEmailGroups(users);

  const handleDeleteUser = async (userId: string) => {
    try {
      await UserService.delete(userId);
      message.success('User deleted successfully');
      await loadUsers();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Failed to delete user'));
    }
  };

  const sendPasswordSetup = async (email: string) => {
    try {
      const response = await apiClient.post<{ detail?: string }>('/auth/forgot-password', { email });
      message.success(response.data?.detail || 'If that account can receive mail, a password setup link was sent.');
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Could not start password setup.'));
    }
  };

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      if (isCreating && selectedUserId?.startsWith('temp-')) {
        // Create new user
        const createData: UserCreate = {
          username: values.username,
          email: values.email,
          password: values.password,
          full_name: values.full_name,
          role: values.role,
        };
        const newUser = await UserService.createForPlatformDirectory(createData);
        setUsers(users.filter(u => u.id !== selectedUserId).concat(newUser));
        setSelectedUserId(newUser.id);
        setIsCreating(false);
        if (values.company_id) {
          try {
            await UserService.addToCompany(newUser.id, values.company_id);
          } catch (membershipErr: unknown) {
            message.warning(getApiErrorMessage(membershipErr, 'User was created, but the company membership was not added.'));
          }
        }
        message.success('User created successfully');
      } else if (selectedUser) {
        // Update existing user
        const updateData: UserUpdate = {
          username: values.username,
          email: values.email,
          full_name: values.full_name,
          is_active: values.is_active,
          role: values.role,
          profile_data: (() => {
            const nested = nestProfilePreferences(
              normalizeProfileData(values.profile_data)
            );
            return Object.keys(nested).length > 0 ? nested : undefined;
          })(),
        };
        const updatedUser = await UserService.updateForPlatformDirectory(selectedUser.id, updateData);
        if (values.company_id) {
          const alreadyMember = (userCompaniesMap[selectedUser.id] || []).some(
            (row) => row.company.id === values.company_id,
          );
          if (!alreadyMember) {
            try {
              await UserService.addToCompany(selectedUser.id, values.company_id);
            } catch (membershipErr: unknown) {
              message.warning(getApiErrorMessage(membershipErr, 'User was saved, but the company membership was not added.'));
            }
          }
        }
        message.success('User updated successfully');
        
        // Update in list
        setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
        setSelectedUserId(updatedUser.id);
      }
      
      await loadUsers();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Failed to save user'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating && selectedUserId?.startsWith('temp-')) {
      // Remove temp item from list
      setUsers(users.filter(u => u.id !== selectedUserId));
    }
    setSelectedUserId(null);
    setIsCreating(false);
    form.resetFields();
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    try {
      await UserService.delete(selectedUser.id);
      message.success('User deleted successfully');
      setSelectedUserId(null);
      setIsCreating(false);
      await loadUsers();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Failed to delete user'));
      throw err;
    }
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (text: string, user: User) => (
        <Button
          type="link"
          onClick={() => handleSelectUser(user)}
          style={{ padding: 0 }}
        >
          <Space>
            <UserOutlined />
            <Text strong>{text}</Text>
          </Space>
        </Button>
      ),
      sorter: (a: User, b: User) => a.username.localeCompare(b.username),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text: string) => (
        <Space>
          <MailOutlined />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (fullName: string) => (
        <Text>{fullName || 'Not set'}</Text>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        return role ? <Tag>{role}</Tag> : <Text type="secondary">No role</Text>;
      },
    },
    {
      title: 'Companies',
      key: 'companies',
      render: (user: User) => {
        const companyData = userCompaniesMap[user.id] || [];
        
        if (companyData.length === 0) {
          return <Text type="secondary">None</Text>;
        }
        return (
          <Space wrap>
            {companyData.map((item) => (
              <Tag key={item.instance.id} title={item.instance.role ? `Membership role: ${item.instance.role}` : undefined}>
                {item.company.name}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          <Text type="secondary">{new Date(date).toLocaleDateString()}</Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (user: User) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleSelectUser(user)}
            title="Edit user"
          />
          <Popconfirm
            title="Are you sure you want to delete this user?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteUser(user.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete user"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const formTabs = [
    {
      key: 'basic',
      label: 'Basic Info',
      children: (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Username"
                rules={[
                  { required: true, message: 'Please enter a username' },
                  { min: 3, message: 'Username must be at least 3 characters' },
                ]}
              >
                <Input placeholder="Enter username" autoComplete="username" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter an email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>
          </Row>
          {isCreating && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter a password' },
                { min: 8, message: 'Password must be at least 8 characters' },
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          )}
          <Form.Item
            name="full_name"
            label="Full Name"
          >
            <Input placeholder="Enter full name" />
          </Form.Item>
          <Form.Item
            name="company_id"
            label="Add company membership"
            extra="This adds a company membership. PUT /users does not store a separate primary company."
          >
            <Select
              placeholder="Select primary company"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {allCompanies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {!isCreating && selectedUser && (
            <Form.Item
              label="Last Login"
            >
              <Text type="secondary">
                {selectedUser.last_login
                  ? new Date(selectedUser.last_login).toLocaleString()
                  : 'Never'}
              </Text>
            </Form.Item>
          )}
        </>
      ),
    },
    {
      key: 'profile',
      label: 'Profile',
      children: (
        <>
          <Form.Item
            name="profile_data"
            label="Profile Data"
          >
            <ProfileDataEditor />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'settings',
      label: 'Settings',
      children: (
        <>
          <Form.Item
            name="role"
            label="Role"
          >
            <Select placeholder="Select role">
              {availableRoles.map((role) => (
                <Option key={role.value} value={role.value}>
                  {role.description}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Form.Item>
          {!isCreating && selectedUser?.email && (
            <Form.Item label="Password setup">
              <Button htmlType="button" onClick={() => void sendPasswordSetup(selectedUser.email)}>
                Email password setup
              </Button>
            </Form.Item>
          )}
          {!isCreating && selectedUser && selectedUser.is_superuser !== undefined && (
            <Form.Item label="Superuser">
              <Text type="secondary">
                {selectedUser.is_superuser ? 'Yes' : 'No'}
              </Text>
            </Form.Item>
          )}
        </>
      ),
    },
    {
      key: 'system',
      label: 'System Info',
      children: (
        <>
          {!isCreating && selectedUser && (
            <>
              <Form.Item label="Created At">
                <Text type="secondary">
                  {new Date(selectedUser.created_at).toLocaleString()}
                </Text>
              </Form.Item>
              <Form.Item label="Updated At">
                <Text type="secondary">
                  {new Date(selectedUser.updated_at).toLocaleString()}
                </Text>
              </Form.Item>
              {selectedUser.is_deleted && (
                <>
                  <Form.Item label="Deleted">
                    <Tag color="red">Yes</Tag>
                  </Form.Item>
                  {selectedUser.deleted_at && (
                    <Form.Item label="Deleted At">
                      <Text type="secondary">
                        {new Date(selectedUser.deleted_at).toLocaleString()}
                      </Text>
                    </Form.Item>
                  )}
                </>
              )}
              {selectedUser.updated_by && (
                <Form.Item label="Updated By">
                  <Text type="secondary">{selectedUser.updated_by}</Text>
                </Form.Item>
              )}
            </>
          )}
        </>
      ),
    },
    {
      key: 'companies',
      label: 'Companies',
      children: selectedUserId && !selectedUserId.startsWith('temp-') ? (
        <UserCompanyTab userId={selectedUserId} />
      ) : (
        <Empty description="Save the user first to manage companies" />
      ),
    },
    {
      key: 'subscriptions',
      label: 'Subscriptions',
      children: selectedUserId && !selectedUserId.startsWith('temp-') ? (
        <UserSubscriptionTab userId={selectedUserId} />
      ) : (
        <Empty description="Save the user first to manage subscriptions" />
      ),
    },
  ];

  const activeUsers = users.filter(user => user.is_active).length;
  const totalUsers = users.length;

  return (
    <div className={`management-page-container ${className || ''}`}>
      <div className="management-page-header">
        <Title level={2}>User Management</Title>
        <Text type="secondary">
          Manage system users, create new accounts, and configure user settings.
        </Text>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
          action={
            <Button size="small" onClick={loadUsers}>
              Retry
            </Button>
          }
        />
      )}

      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Users"
              value={totalUsers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active Users"
              value={activeUsers}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Inactive Users"
              value={totalUsers - activeUsers}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <SplitPaneLayout
        leftContent={
          <Card
            className="list-card"
            title={
              <Space>
                <Title level={4} style={{ margin: 0 }}>Users</Title>
                <Tag>{filteredUsers.length}</Tag>
              </Space>
            }
            extra={
              <Space>
                <Input
                  placeholder="Search name, email, or company"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
                <Button icon={<ReloadOutlined />} onClick={loadUsers} size="small">
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateUser}
                >
                  New
                </Button>
              </Space>
            }
          >
            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Select
                placeholder="Filter by Role"
                style={{ width: 150 }}
                allowClear
                onChange={(value) => handleFilterChange('role', value)}
                value={filters.role}
                size="small"
              >
                <Option value="superuser">Superuser</Option>
                <Option value="platform_admin">Platform admin</Option>
                <Option value="admin">Admin</Option>
                <Option value="architect">Architect</Option>
                <Option value="manager">Manager</Option>
                <Option value="user">User</Option>
                <Option value="operator">Operator</Option>
                <Option value="viewer">Viewer</Option>
              </Select>
              <Select
                placeholder="Filter by Status"
                style={{ width: 120 }}
                allowClear
                onChange={(value) => handleFilterChange('is_active', value)}
                value={filters.is_active}
                size="small"
              >
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
              <Select
                placeholder="Filter by company"
                style={{ minWidth: 180 }}
                allowClear
                size="small"
                value={companyFilter}
                onChange={(value) => setCompanyFilter(value)}
                options={allCompanies.map((company) => ({ value: company.id, label: company.name }))}
              />
            </div>
            {directoryTruncated || companyListTruncated || membershipTruncated ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="This directory is capped."
                description="Users and the company filter stop at 1,000 rows. Company memberships stop at 5,000. Narrow the role or status filter to load a smaller user set."
              />
            ) : null}
            {duplicateEmails.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${duplicateEmails.length} email address${duplicateEmails.length === 1 ? '' : 'es'} appear on more than one user`}
                description={ACCOUNT_MERGE_REFUSAL}
              />
            ) : null}
            <Table
              columns={columns.filter(col => col.key !== 'actions')}
              dataSource={filteredUsers}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} users`,
                position: ['bottomCenter'],
              }}
              scroll={{ x: 'max-content', y: 'calc(100vh - 400px)' }}
              onRow={(record) => ({
                onClick: () => handleSelectUser(record),
                style: {
                  cursor: 'pointer',
                  backgroundColor: selectedUserId === record.id ? '#e6f7ff' : 'transparent',
                },
              })}
              rowClassName={(record) =>
                selectedUserId === record.id ? 'selected-row' : ''
              }
            />
          </Card>
        }
        rightContent={
          selectedUser || isCreating ? (
            <ManagementForm
              title={isCreating ? 'New User' : `Edit User: ${selectedUser?.username || ''}`}
              isCreating={isCreating}
              isLoading={false}
              isSaving={isSaving}
              form={form}
              tabs={formTabs}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={!isCreating ? handleDelete : undefined}
              showDelete={!isCreating}
            />
          ) : (
            <Card className="details-card">
              <Empty
                description="Select a user or click 'New' to create one"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          )
        }
      />
    </div>
  );
};

